import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Hotel as HotelIcon,
  Event as EventIcon,
  Campaign as PromoteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { partnershipsService, PartnershipApplication, ApplicationStatus } from '../../services/partnershipsService';
import ConfirmDialog from '@/components/common/ConfirmDialog';

// Helper function to linkify text (URLs and social media handles)
const linkifyText = (text: string) => {
  if (!text) return null;

  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  lines.forEach((line, lineIndex) => {
    // Pattern for Instagram handles
    const instagramMatch = line.match(/Instagram:\s*@?([a-zA-Z0-9._]+)/i);
    // Pattern for TikTok handles
    const tiktokMatch = line.match(/TikTok:\s*@?([a-zA-Z0-9._]+)/i);
    // Pattern for YouTube URLs
    const youtubeMatch = line.match(/YouTube:\s*(https?:\/\/[^\s]+)/i);
    // Pattern for generic URLs
    const urlMatch = line.match(/(https?:\/\/[^\s]+)/);

    if (instagramMatch) {
      const handle = instagramMatch[1];
      elements.push(
        <Box key={lineIndex} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="body2" component="span">
            Instagram:
          </Typography>
          <Link
            href={`https://instagram.com/${handle}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            @{handle}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Box>
      );
    } else if (tiktokMatch) {
      const handle = tiktokMatch[1];
      elements.push(
        <Box key={lineIndex} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="body2" component="span">
            TikTok:
          </Typography>
          <Link
            href={`https://tiktok.com/@${handle}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {handle}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Box>
      );
    } else if (youtubeMatch) {
      const url = youtubeMatch[1];
      elements.push(
        <Box key={lineIndex} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="body2" component="span">
            YouTube:
          </Typography>
          <Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {url}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Box>
      );
    } else if (urlMatch) {
      const url = urlMatch[1];
      elements.push(
        <Box key={lineIndex} sx={{ mb: 0.5 }}>
          <Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {url}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Box>
      );
    } else {
      elements.push(
        <Typography key={lineIndex} variant="body2" sx={{ mb: 0.5 }}>
          {line}
        </Typography>
      );
    }
  });

  return <>{elements}</>;
};

const PartnershipApplicationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ApplicationStatus | 'all'>('pending');
  const [applications, setApplications] = useState<PartnershipApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ totalPending: 0, totalApproved: 0, totalRejected: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<PartnershipApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    severity: 'question' as 'warning' | 'error' | 'info' | 'question',
  });

  useEffect(() => {
    fetchApplications();
  }, [activeTab, searchTerm, typeFilter]);

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);

    // Fetch filtered applications for display
    const result = await partnershipsService.getApplications({
      status: activeTab === 'all' ? undefined : activeTab,
      search: searchTerm || undefined,
      partnershipType: typeFilter === 'all' ? undefined : (typeFilter as any),
      limit: 100,
    });

    // Fetch unfiltered summary for accurate badge counts
    const summaryResult = await partnershipsService.getApplications({
      limit: 1, // We only need the summary, not the applications
    });

    if (result.success && result.data) {
      setApplications(result.data.applications);

      // Use the unfiltered summary for accurate counts
      if (summaryResult.success && summaryResult.data) {
        setSummary(summaryResult.data.summary);
      } else {
        setSummary(result.data.summary);
      }
    } else {
      setError(result.error || 'Failed to load applications');
    }

    setLoading(false);
  };

  const handleApprove = async (application: PartnershipApplication) => {
    setConfirmDialog({
      open: true,
      title: 'Approve Partnership Application',
      message: `Are you sure you want to approve the partnership application for ${application.companyName || application.applicantName}? This will activate the partnership account.`,
      severity: 'question',
      onConfirm: async () => {
        setActionLoading(true);
        setError(null);

        const result = await partnershipsService.approveApplication(application.id);

        if (result.success) {
          setSuccessMessage('Application approved successfully!');
          fetchApplications();
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          setError(result.error || 'Failed to approve application');
        }

        setActionLoading(false);
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  const handleRejectClick = (application: PartnershipApplication) => {
    setSelectedApplication(application);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedApplication) return;

    setActionLoading(true);
    setError(null);

    const result = await partnershipsService.rejectApplication(
      selectedApplication.id,
      rejectionReason
    );

    if (result.success) {
      setSuccessMessage('Application rejected');
      setRejectDialogOpen(false);
      fetchApplications();
      setTimeout(() => setSuccessMessage(null), 3000);
    } else {
      setError(result.error || 'Failed to reject application');
    }

    setActionLoading(false);
  };

  const handleViewDetails = (application: PartnershipApplication) => {
    setSelectedApplication(application);
    setViewDialogOpen(true);
  };

  const getPartnershipIcon = (type: string) => {
    switch (type) {
      case 'hotel':
        return <HotelIcon />;
      case 'event_planner':
        return <EventIcon />;
      case 'promoter':
        return <PromoteIcon />;
      default:
        return <BusinessIcon />;
    }
  };

  const getApplicationTypeIcon = (type: string) => {
    return type === 'corporate' ? <BusinessIcon /> : <PersonIcon />;
  };

  const getStatusColor = (status: ApplicationStatus): 'warning' | 'success' | 'error' => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Partnership Applications
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchApplications}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} mb={3}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Pending
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="warning.main">
            {summary.totalPending}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Approved
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {summary.totalApproved}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Rejected
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="error.main">
            {summary.totalRejected}
          </Typography>
        </Paper>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label={`All (${summary.totalPending + summary.totalApproved + summary.totalRejected})`} value="all" />
          <Tab label={`Pending (${summary.totalPending})`} value="pending" />
          <Tab label={`Approved (${summary.totalApproved})`} value="approved" />
          <Tab label={`Rejected (${summary.totalRejected})`} value="rejected" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2}>
        <TextField
          placeholder="Search by company/name..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          label="Partnership Type"
          size="small"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All Types</MenuItem>
          <MenuItem value="hotel">Hotel</MenuItem>
          <MenuItem value="event_planner">Event Planner</MenuItem>
          <MenuItem value="promoter">Promoter</MenuItem>
        </TextField>
      </Box>

      {/* Applications Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company/Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Application Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : applications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">No applications found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              applications.map((app) => (
                <TableRow key={app.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getPartnershipIcon(app.partnershipType)}
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {app.companyName || app.applicantName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {app.billingEmail || app.applicantEmail}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={app.partnershipTypeName}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getApplicationTypeIcon(app.applicationType)}
                      <Typography variant="body2" textTransform="capitalize">
                        {app.applicationType}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={app.status}
                      color={getStatusColor(app.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(app.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewDetails(app)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {app.status === 'pending' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(app)}
                            disabled={actionLoading}
                          >
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectClick(app)}
                            disabled={actionLoading}
                          >
                            <RejectIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Application Details</DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedApplication.companyName || selectedApplication.applicantName}
              </Typography>

              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mt={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Partnership Type
                  </Typography>
                  <Typography>{selectedApplication.partnershipTypeName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Application Type
                  </Typography>
                  <Typography textTransform="capitalize">{selectedApplication.applicationType}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography>{selectedApplication.billingEmail || selectedApplication.applicantEmail}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedApplication.status}
                    color={getStatusColor(selectedApplication.status)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
                {selectedApplication.applicationType === 'corporate' && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      CAC Registration Number
                    </Typography>
                    <Typography>
                      {selectedApplication.companyRegistrationNumber || (
                        <Typography component="span" color="error.main" variant="body2">
                          Not provided
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                )}
                {selectedApplication.city && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Location
                    </Typography>
                    <Typography>
                      {selectedApplication.city}, {selectedApplication.state}
                    </Typography>
                  </Box>
                )}
                {selectedApplication.industry && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Industry
                    </Typography>
                    <Typography>{selectedApplication.industry}</Typography>
                  </Box>
                )}
                {selectedApplication.expectedMonthlyReferrals && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Social Media Followers
                    </Typography>
                    <Typography>
                      {Number(selectedApplication.expectedMonthlyReferrals).toLocaleString()}
                      <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                        (Note: Backend field mapping issue)
                      </Typography>
                    </Typography>
                  </Box>
                )}
              </Box>

              {selectedApplication.notes && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Additional Notes
                  </Typography>
                  <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                    {linkifyText(selectedApplication.notes)}
                  </Paper>
                </Box>
              )}

              {selectedApplication.rejectionReason && (
                <Box mt={2}>
                  <Typography variant="caption" color="error">
                    Rejection Reason
                  </Typography>
                  <Paper sx={{ p: 2, mt: 1, bgcolor: 'error.lighter', borderLeft: 3, borderColor: 'error.main' }}>
                    <Typography variant="body2">{selectedApplication.rejectionReason}</Typography>
                  </Paper>
                </Box>
              )}

              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Submitted
                </Typography>
                <Typography>{new Date(selectedApplication.createdAt).toLocaleString()}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to reject the application from{' '}
            <strong>{selectedApplication?.companyName || selectedApplication?.applicantName}</strong>?
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason (Optional)"
            fullWidth
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Provide a reason for rejection..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleRejectConfirm} color="error" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Reject Application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        severity={confirmDialog.severity}
        confirmText="Approve"
        cancelText="Cancel"
        loading={actionLoading}
      />
    </Box>
  );
};

export default PartnershipApplicationsPage;
